const DAY_NAMES = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

const COARSE_TIME_WINDOWS = {
  morning: { start: 8 * 60, end: 12 * 60 },
  afternoon: { start: 12 * 60, end: 17 * 60 },
  evening: { start: 17 * 60, end: 21 * 60 },
};

const DEFAULT_SLOT_MINUTES = 5;
const DEFAULT_BEAM_WIDTH = 300;
const DEFAULT_OPTIONS_PER_STATE = 28;
const DEFAULT_LARGE_SEARCH_STARTS_PER_GAP = 9;

const finiteNumber = (value) => {
  if (value == null || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const parseMinutes = (value) => {
  if (Number.isFinite(Number(value)) && typeof value !== "string") return Number(value);
  if (typeof value !== "string") return null;
  const match = value
    .trim()
    .match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?$/i);
  if (!match) return null;
  let hour = Number(match[1]);
  const minute = Number(match[2] || 0);
  const meridiem = normalizeText(match[3]);
  if (minute < 0 || minute > 59) return null;
  if (meridiem) {
    if (hour < 1 || hour > 12) return null;
    if (hour === 12) hour = 0;
    if (meridiem === "pm") hour += 12;
  }
  if (hour < 0 || hour > 24 || (hour === 24 && minute !== 0)) return null;
  return hour * 60 + minute;
};

const mergeBlocks = (blocks) => {
  const sorted = (blocks || [])
    .map((block) => ({
      start: finiteNumber(block?.start),
      end: finiteNumber(block?.end),
    }))
    .filter(
      (block) =>
        block.start != null &&
        block.end != null &&
        block.start >= 0 &&
        block.end <= 24 * 60 &&
        block.end > block.start
    )
    .sort((a, b) => a.start - b.start || a.end - b.end);

  const merged = [];
  sorted.forEach((block) => {
    const previous = merged[merged.length - 1];
    if (!previous || block.start > previous.end) {
      merged.push({ ...block });
      return;
    }
    previous.end = Math.max(previous.end, block.end);
  });
  return merged;
};

const dayValue = (availability, dayName, dayIndex) => {
  if (!availability || typeof availability !== "object") return undefined;
  const short = dayName.slice(0, 3);
  const aliases = [
    dayName,
    dayName.toLowerCase(),
    short,
    short.toLowerCase(),
    String(dayIndex),
  ];
  for (const alias of aliases) {
    if (Object.prototype.hasOwnProperty.call(availability, alias)) {
      return availability[alias];
    }
  }
  return undefined;
};

/**
 * Converts both exact time ranges and the legacy Morning/Afternoon/Evening
 * family availability into a day-indexed map. `restricted` distinguishes a
 * genuinely empty availability record from no availability information.
 */
export const normalizeOptimizerAvailability = (availability) => {
  if (!availability || typeof availability !== "object") {
    return { byDay: {}, restricted: false };
  }

  const byDay = {};
  let recognized = false;
  DAY_NAMES.forEach((dayName, dayIndex) => {
    const raw = dayValue(availability, dayName, dayIndex);
    const blocks = [];
    if (Array.isArray(raw)) {
      recognized = true;
      const tupleStart = raw.length === 2 ? parseMinutes(raw[0]) : null;
      const tupleEnd = raw.length === 2 ? parseMinutes(raw[1]) : null;
      const entries =
        tupleStart != null && tupleEnd != null ? [[raw[0], raw[1]]] : raw;
      entries.forEach((entry) => {
        if (!entry) return;
        let start = null;
        let end = null;
        if (Array.isArray(entry)) {
          start = parseMinutes(entry[0]);
          end = parseMinutes(entry[1]);
        } else if (typeof entry === "string" && entry.includes("-")) {
          const parts = entry.split("-");
          start = parseMinutes(parts[0]);
          end = parseMinutes(parts.slice(1).join("-"));
        } else if (typeof entry === "object") {
          start = parseMinutes(entry.start ?? entry.StartTime ?? entry.startTime);
          end = parseMinutes(entry.end ?? entry.EndTime ?? entry.endTime);
        }
        if (start != null && end != null && end > start) blocks.push({ start, end });
      });
    } else if (typeof raw === "string") {
      recognized = true;
      const parts = raw.split("-");
      if (parts.length >= 2) {
        const start = parseMinutes(parts[0]);
        const end = parseMinutes(parts.slice(1).join("-"));
        if (start != null && end != null && end > start) blocks.push({ start, end });
      }
    } else if (raw && typeof raw === "object") {
      const directStart = parseMinutes(raw.start ?? raw.StartTime ?? raw.startTime);
      const directEnd = parseMinutes(raw.end ?? raw.EndTime ?? raw.endTime);
      if (directStart != null || directEnd != null) {
        recognized = true;
        if (directStart != null && directEnd != null && directEnd > directStart) {
          blocks.push({ start: directStart, end: directEnd });
        }
      }
      const coarseKeys = Object.keys(COARSE_TIME_WINDOWS);
      const hasCoarseShape = coarseKeys.some((key) =>
        Object.keys(raw).some((rawKey) => normalizeText(rawKey) === key)
      );
      if (hasCoarseShape) {
        recognized = true;
        Object.entries(raw).forEach(([label, enabled]) => {
          const window = COARSE_TIME_WINDOWS[normalizeText(label)];
          if (window && enabled === true) blocks.push({ ...window });
        });
      }
    }
    byDay[dayIndex] = mergeBlocks(blocks);
  });

  return { byDay, restricted: recognized };
};

export const createCanonicalStudentKey = (student) => {
  if (!student) return "";
  if (student.placementKey != null && String(student.placementKey).trim()) {
    return String(student.placementKey);
  }
  const familyId = student.familyId ?? student.familyID ?? student.FamilyID;
  const studentId = student.studentId ?? student.studentID ?? student.StudentID;
  if (familyId != null && studentId != null) return `${familyId}-${studentId}`;
  if (student.id != null && String(student.id).trim()) return String(student.id);
  if (studentId != null && String(studentId).trim()) return String(studentId);
  return String(student.studentName || student.familyName || "");
};

const instrumentSet = (items) => {
  const values = Array.isArray(items) ? items : items ? [items] : [];
  const result = new Set();
  values.forEach((item) => {
    if (typeof item === "string") {
      const value = normalizeText(item);
      if (value) result.add(value);
      return;
    }
    if (!item || typeof item !== "object") return;
    [
      item.id,
      item._id,
      item.instrumentId,
      item.InstrumentID,
      item.parentInstrument,
      item.instrument,
      item.name,
      item.label,
    ].forEach((value) => {
      const normalized = normalizeText(value);
      if (normalized) result.add(normalized);
    });
  });
  return result;
};

export const haveCompatibleInstruments = (
  teacherInstruments,
  studentInstruments
) => {
  const teacherKeys = instrumentSet(teacherInstruments);
  const studentKeys = instrumentSet(studentInstruments);
  if (!teacherKeys.size || !studentKeys.size) return false;
  for (const key of studentKeys) {
    if (teacherKeys.has(key)) return true;
  }
  return false;
};

const teacherCanTeach = (teacher, student) => {
  if (!teacher.instrumentKeys.size || !student.instrumentKeys.size) return false;
  for (const instrument of student.instrumentKeys) {
    if (teacher.instrumentKeys.has(instrument)) return true;
  }
  return false;
};

const hasSharedAvailabilityWindowInBlock = (student, dayIndex, teacherBlock) => {
  if (!student.availabilityRestricted) {
    return teacherBlock.end - teacherBlock.start >= student.duration;
  }
  const studentBlocks = student.availabilityByDay[dayIndex] || [];
  return studentBlocks.some(
    (studentBlock) =>
      Math.min(teacherBlock.end, studentBlock.end) -
        Math.max(teacherBlock.start, studentBlock.start) >=
      student.duration
  );
};

const prepareTeacher = (teacher) => {
  const id = String(
    teacher?.id ?? teacher?.teacherId ?? teacher?.TeacherID ?? teacher?.value ?? ""
  );
  const availabilitySource =
    teacher?.availabilityByDay ?? teacher?.availability ?? teacher?.AvailabilityDetails;
  const normalized = normalizeOptimizerAvailability(availabilitySource);
  return {
    ...teacher,
    id,
    homeKey: teacher?.homeKey ? String(teacher.homeKey) : null,
    instrumentKeys: instrumentSet(
      teacher?.instrumentKeys
        ? Array.from(teacher.instrumentKeys)
        : teacher?.instruments ?? teacher?.InstrumentList ?? teacher?.instrumentList
    ),
    availabilityByDay: normalized.byDay,
  };
};

const teacherDisplayName = (teacher) => {
  const name =
    teacher?.name ??
    teacher?.Name ??
    teacher?.teacherName ??
    teacher?.TeacherName;
  return name != null && String(name).trim()
    ? String(name).trim()
    : String(teacher?.id || "Unknown teacher");
};

const prepareStudent = (student) => {
  const availabilitySource =
    student?.availabilityByDay ?? student?.availability ?? student?.initialAvailability;
  const normalized = normalizeOptimizerAvailability(availabilitySource);
  return {
    ...student,
    key: String(student?.key || createCanonicalStudentKey(student)),
    placeKey: student?.placeKey ? String(student.placeKey) : null,
    duration: Math.max(
      DEFAULT_SLOT_MINUTES,
      finiteNumber(student?.duration ?? student?.lessonLength) || 30
    ),
    instrumentKeys: instrumentSet(
      student?.instrumentKeys
        ? Array.from(student.instrumentKeys)
        : student?.instruments ?? student?.instrumentList ?? student?.instrument
    ),
    availabilityByDay: normalized.byDay,
    availabilityRestricted:
      typeof student?.availabilityRestricted === "boolean"
        ? student.availabilityRestricted
        : normalized.restricted,
  };
};

const prepareEvent = (event, fallbackId) => {
  const teacherId = String(
    event?.teacherId ?? event?.teacherID ?? event?.TeacherID ?? ""
  );
  const dayIndex = Number(event?.dayIndex);
  const start = finiteNumber(event?.start ?? event?.startMinutes);
  const explicitEnd = finiteNumber(event?.end ?? event?.endMinutes);
  const duration = finiteNumber(event?.duration ?? event?.lessonLength) || 30;
  const end = explicitEnd != null ? explicitEnd : start != null ? start + duration : null;
  return {
    ...event,
    id: String(event?.id || fallbackId),
    teacherId,
    dayIndex,
    start,
    end,
    placeKey: event?.placeKey ? String(event.placeKey) : null,
    studentKey: event?.studentKey || createCanonicalStudentKey(event),
    optimized: Boolean(event?.optimized),
  };
};

const prepareFixedEvents = (events) => {
  const seen = new Set();
  const prepared = [];
  (events || []).forEach((event, index) => {
    const next = prepareEvent(event, `fixed-${index}`);
    if (
      !next.teacherId ||
      next.dayIndex < 0 ||
      next.dayIndex > 6 ||
      next.start == null ||
      next.end == null ||
      next.end <= next.start
    ) {
      return;
    }
    const duplicateKey = [
      next.teacherId,
      next.dayIndex,
      next.start,
      next.end,
      next.placeKey || "missing",
      next.studentKey || "",
    ].join("|");
    if (seen.has(duplicateKey)) return;
    seen.add(duplicateKey);
    prepared.push(next);
  });
  return prepared;
};

const timelineKey = (teacherId, dayIndex) => `${teacherId}|${dayIndex}`;

const blockRouteStops = (
  teacher,
  dayIndex,
  block,
  blockIndex,
  dayEvents,
  travelMinutes,
  paddingMinutes = 0,
  maxDriveMinutes = null
) => {
  const sortedDayEvents = (dayEvents || []).slice().sort(
    (a, b) =>
      a.start - b.start ||
      a.end - b.end ||
      String(a.id || "").localeCompare(String(b.id || ""))
  );
  const touchingEvents = sortedDayEvents
    .filter((event) => event.end >= block.start && event.start <= block.end)
    .slice();
  const events = touchingEvents.slice();
  const previous = sortedDayEvents
    .filter((event) => event.end < block.start)
    .sort((a, b) => b.end - a.end || b.start - a.start)[0];
  const next = sortedDayEvents.find((event) => event.start > block.end);
  const canUseHomeBoundary = (event, beforeBlock) => {
    if (!event || typeof travelMinutes !== "function") return !event;
    const drive = beforeBlock
      ? safeTravel(travelMinutes, event.placeKey, teacher.homeKey)
      : safeTravel(travelMinutes, teacher.homeKey, event.placeKey);
    if (
      drive == null ||
      (maxDriveMinutes != null && drive > maxDriveMinutes)
    ) {
      return false;
    }
    return beforeBlock
      ? event.end + drive + paddingMinutes <= block.start
      : block.end + drive + paddingMinutes <= event.start;
  };
  if (previous && !canUseHomeBoundary(previous, true)) events.push(previous);
  if (next && !canUseHomeBoundary(next, false)) events.push(next);
  events.sort(
    (a, b) =>
      a.start - b.start ||
      a.end - b.end ||
      String(a.id || "").localeCompare(String(b.id || ""))
  );
  const stops = [];
  const first = events[0];
  const fixedEventAnchorsStart =
    first && !first.optimized && first.start <= block.start;
  if (!fixedEventAnchorsStart) {
    stops.push({
      id: `home-start-${teacher.id}-${dayIndex}-${blockIndex}`,
      end: block.start,
      placeKey: teacher.homeKey,
      boundary: true,
    });
  }
  stops.push(...events);
  const last = events[events.length - 1];
  const fixedEventAnchorsEnd = last && !last.optimized && last.end >= block.end;
  if (!fixedEventAnchorsEnd) {
    stops.push({
      id: `home-end-${teacher.id}-${dayIndex}-${blockIndex}`,
      start: block.end,
      placeKey: teacher.homeKey,
      boundary: true,
    });
  }
  return { events: touchingEvents, routeEvents: events, stops };
};

const buildTimelines = (events) => {
  const timelines = {};
  (events || []).forEach((event) => {
    const key = timelineKey(event.teacherId, event.dayIndex);
    if (!timelines[key]) timelines[key] = [];
    timelines[key].push(event);
  });
  Object.keys(timelines).forEach((key) => {
    timelines[key].sort((a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id));
  });
  return timelines;
};

const safeTravel = (travelMinutes, fromKey, toKey) => {
  if (!fromKey || !toKey) return null;
  if (String(fromKey) === String(toKey)) return 0;
  const value = travelMinutes(fromKey, toKey);
  if (value == null || value === "") return null;
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
};

const ceilToSlot = (minutes, slotMinutes) =>
  Math.ceil((minutes - 1e-9) / slotMinutes) * slotMinutes;
const floorToSlot = (minutes, slotMinutes) =>
  Math.floor((minutes + 1e-9) / slotMinutes) * slotMinutes;

const spreadIndexes = (length) => {
  if (length <= 0) return [];
  const indexes = [];
  const added = new Set();
  const addIndex = (index) => {
    if (index < 0 || index >= length || added.has(index)) return;
    added.add(index);
    indexes.push(index);
  };
  addIndex(0);
  addIndex(length - 1);
  const intervals = [[0, length - 1]];
  while (intervals.length) {
    const [start, end] = intervals.shift();
    if (end - start <= 1) continue;
    const middle = Math.floor((start + end) / 2);
    addIndex(middle);
    intervals.push([start, middle], [middle, end]);
  }
  return indexes;
};

const sampleStartMinutes = ({
  earliest,
  latest,
  slotMinutes,
  incumbentStart,
  maximum = Infinity,
}) => {
  const count = Math.floor((latest - earliest) / slotMinutes) + 1;
  if (count <= 0) return [];
  if (!Number.isFinite(maximum) || count <= maximum) {
    return Array.from({ length: count }, (_, index) => earliest + index * slotMinutes);
  }
  const selected = [];
  const seen = new Set();
  const add = (value) => {
    if (
      !Number.isFinite(value) ||
      value < earliest ||
      value > latest ||
      Math.abs((value - earliest) % slotMinutes) > 1e-9 ||
      seen.has(value) ||
      selected.length >= maximum
    ) {
      return;
    }
    seen.add(value);
    selected.push(value);
  };
  add(incumbentStart);
  spreadIndexes(count).forEach((index) => add(earliest + index * slotMinutes));
  return selected.sort((a, b) => a - b);
};

const intersects = (left, right) => ({
  start: Math.max(left.start, right.start),
  end: Math.min(left.end, right.end),
});

const optionComparator = (a, b) =>
  a.driveDelta - b.driveDelta ||
  a.idleDelta - b.idleDelta ||
  a.churnDelta - b.churnDelta ||
  a.teacherId.localeCompare(b.teacherId) ||
  a.dayIndex - b.dayIndex ||
  a.startMinutes - b.startMinutes;

const eventIdentity = (event) => event?.id || `${event?.teacherId}-${event?.start}-${event?.end}`;

/** Enumerates only hard-constraint-valid insertions into a prepared timeline. */
export const enumeratePlacementOptions = ({
  student: rawStudent,
  teachers: rawTeachers,
  timelines = {},
  travelMinutes,
  slotMinutes = DEFAULT_SLOT_MINUTES,
  paddingMinutes = 0,
  maxDriveMinutes = null,
  maxStartsPerGap = Infinity,
}) => {
  const student = rawStudent?.instrumentKeys ? rawStudent : prepareStudent(rawStudent);
  const teachers = (rawTeachers || []).map((teacher) =>
    teacher?.instrumentKeys ? teacher : prepareTeacher(teacher)
  );
  if (!student.key || !student.placeKey || typeof travelMinutes !== "function") return [];

  const options = [];
  teachers.forEach((teacher) => {
    if (!teacher.id || !teacher.homeKey || !teacherCanTeach(teacher, student)) return;
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const teacherBlocks = teacher.availabilityByDay[dayIndex] || [];
      if (!teacherBlocks.length) continue;
      const studentBlocks = student.availabilityRestricted
        ? student.availabilityByDay[dayIndex] || []
        : null;
      if (student.availabilityRestricted && !studentBlocks.length) continue;
      const dayEvents = (timelines[timelineKey(teacher.id, dayIndex)] || []).slice();

      teacherBlocks.forEach((teacherBlock, blockIndex) => {
        const { events: existingBlockEvents, stops: boundaries } = blockRouteStops(
          teacher,
          dayIndex,
          teacherBlock,
          blockIndex,
          dayEvents,
          travelMinutes,
          paddingMinutes,
          maxDriveMinutes
        );
        const allowedBlocks = studentBlocks
          ? studentBlocks
              .map((block) => intersects(block, teacherBlock))
              .filter((block) => block.end - block.start >= student.duration)
          : [teacherBlock];
        if (!allowedBlocks.length) return;

        for (let index = 0; index < boundaries.length - 1; index += 1) {
          const previous = boundaries[index];
          const next = boundaries[index + 1];
          const previousEnd = previous.end;
          const nextStart = next.start;
          if (previousEnd == null || nextStart == null || nextStart <= previousEnd) continue;

          const driveIn = safeTravel(travelMinutes, previous.placeKey, student.placeKey);
          const driveOut = safeTravel(travelMinutes, student.placeKey, next.placeKey);
          const directDrive = safeTravel(travelMinutes, previous.placeKey, next.placeKey);
          if (driveIn == null || driveOut == null || directDrive == null) continue;
          if (
            maxDriveMinutes != null &&
            (driveIn > maxDriveMinutes || driveOut > maxDriveMinutes)
          ) {
            continue;
          }

          allowedBlocks.forEach((allowed) => {
            const earliest = ceilToSlot(
              Math.max(allowed.start, previousEnd + driveIn + paddingMinutes),
              slotMinutes
            );
            const latest = floorToSlot(
              Math.min(
                allowed.end - student.duration,
                nextStart - driveOut - paddingMinutes - student.duration
              ),
              slotMinutes
            );
            if (latest < earliest) return;

            const incumbent = student.incumbentPlacement;
            sampleStartMinutes({
              earliest,
              latest,
              slotMinutes,
              incumbentStart:
                incumbent &&
                String(incumbent.teacherId) === teacher.id &&
                Number(incumbent.dayIndex) === dayIndex
                  ? Number(incumbent.startMinutes)
                  : null,
              maximum: maxStartsPerGap,
            }).forEach((startMinutes) => {
              const endMinutes = startMinutes + student.duration;
              const idleBefore =
                existingBlockEvents.length &&
                !previous.boundary &&
                !next.boundary
                  ? Math.max(0, nextStart - previousEnd - directDrive)
                  : 0;
              const idleAfter =
                (!previous.boundary
                  ? Math.max(0, startMinutes - previousEnd - driveIn)
                  : 0) +
                (!next.boundary
                  ? Math.max(0, nextStart - endMinutes - driveOut)
                  : 0);
              const isIncumbent =
                incumbent &&
                String(incumbent.teacherId) === teacher.id &&
                Number(incumbent.dayIndex) === dayIndex &&
                Number(incumbent.startMinutes) === startMinutes;
              options.push({
                teacherId: teacher.id,
                dayIndex,
                blockIndex,
                startMinutes,
                endMinutes,
                placeKey: student.placeKey,
                driveIn,
                driveOut,
                directDrive,
                driveDelta:
                  driveIn +
                  driveOut -
                  (existingBlockEvents.length ? directDrive : 0),
                idleDelta: idleAfter - idleBefore,
                churnDelta: isIncumbent ? 0 : incumbent ? 1 : 0,
                previousId: eventIdentity(previous),
                nextId: eventIdentity(next),
              });
            });
          });
        }
      });
    }
  });

  return options.sort(optionComparator);
};

const insertOption = (state, student, option) => {
  const key = timelineKey(option.teacherId, option.dayIndex);
  const activeTeacherDays = new Set(state.activeTeacherDays);
  activeTeacherDays.add(key);
  const event = prepareEvent(
    {
      id: `optimized:${student.key}`,
      studentKey: student.key,
      teacherId: option.teacherId,
      dayIndex: option.dayIndex,
      start: option.startMinutes,
      end: option.endMinutes,
      placeKey: student.placeKey,
      optimized: true,
    },
    `optimized:${student.key}`
  );
  const nextTimeline = [...(state.timelines[key] || []), event].sort(
    (a, b) => a.start - b.start || a.end - b.end || a.id.localeCompare(b.id)
  );
  return {
    ...state,
    timelines: { ...state.timelines, [key]: nextTimeline },
    placements: [
      ...state.placements,
      {
        studentKey: student.key,
        teacherId: option.teacherId,
        dayIndex: option.dayIndex,
        startMinutes: option.startMinutes,
        endMinutes: option.endMinutes,
        placeKey: student.placeKey,
        duration: student.duration,
        driveDelta: option.driveDelta,
      },
    ],
    assignedKeys: new Set([...state.assignedKeys, student.key]),
    scheduledCount: state.scheduledCount + 1,
    driveMinutes: state.driveMinutes + option.driveDelta,
    idleMinutes: state.idleMinutes + option.idleDelta,
    churnCount: state.churnCount + option.churnDelta,
    activeTeacherDays,
    activeTeacherDayCount: activeTeacherDays.size,
    tieKey: `${state.tieKey}|${student.key}:${option.teacherId}:${option.dayIndex}:${option.startMinutes}`,
  };
};

const stateComparator = (a, b) =>
  b.scheduledCount - a.scheduledCount ||
  a.driveMinutes - b.driveMinutes ||
  a.idleMinutes - b.idleMinutes ||
  a.activeTeacherDayCount - b.activeTeacherDayCount ||
  a.churnCount - b.churnCount ||
  a.tieKey.localeCompare(b.tieKey);

const selectDiverseOptions = (options, maximum) => {
  // A cheap insertion can still be a poor choice if its start time leaves no
  // useful opening for a later student. Keep representatives from every
  // teacher/day and route gap, then spread the remaining choices across each
  // gap's full time range instead of retaining only the earliest tied starts.
  const bestByTeacherDay = new Map();
  options.forEach((option) => {
    const key = `${option.teacherId}|${option.dayIndex}`;
    if (!bestByTeacherDay.has(key)) bestByTeacherDay.set(key, option);
  });

  const optionsByGap = new Map();
  options.forEach((option) => {
    const key = [
      option.teacherId,
      option.dayIndex,
      option.blockIndex,
      option.previousId,
      option.nextId,
    ].join("|");
    if (!optionsByGap.has(key)) optionsByGap.set(key, []);
    optionsByGap.get(key).push(option);
  });
  const groups = Array.from(optionsByGap.values())
    .map((group) => group.sort((a, b) => a.startMinutes - b.startMinutes))
    .sort((a, b) => optionComparator(a[0], b[0]));

  // Never let the generic cap erase an entire teacher/day. Preserve up to two
  // caps' worth of structural route gaps as well; the beam still bounds the
  // number of states retained after expansion.
  const limit = Math.max(
    maximum,
    bestByTeacherDay.size,
    Math.min(groups.length, maximum * 2)
  );
  if (options.length <= limit) return options;
  const selected = [];
  const seen = new Set();
  const add = (option) => {
    const key = [
      option.teacherId,
      option.dayIndex,
      option.blockIndex,
      option.previousId,
      option.nextId,
      option.startMinutes,
    ].join("|");
    if (seen.has(key) || selected.length >= limit) return;
    seen.add(key);
    selected.push(option);
  };

  Array.from(bestByTeacherDay.values()).sort(optionComparator).forEach(add);

  groups.forEach((group) => add(group.slice().sort(optionComparator)[0]));
  options.slice(0, Math.max(1, Math.floor(limit / 5))).forEach(add);

  const groupIndexes = groups.map((group) => spreadIndexes(group.length));
  const maximumDepth = Math.max(0, ...groupIndexes.map((indexes) => indexes.length));
  for (let depth = 0; depth < maximumDepth && selected.length < limit; depth += 1) {
    groups.forEach((group, groupIndex) => {
      const optionIndex = groupIndexes[groupIndex][depth];
      if (optionIndex != null) add(group[optionIndex]);
    });
  }

  options.forEach(add);
  return selected.sort(optionComparator);
};

const pruneBeam = (states, beamWidth) => {
  const sorted = states.sort(stateComparator);
  if (sorted.length <= beamWidth) return sorted;
  const maximumCount = sorted[0].scheduledCount;
  const quotas = new Map([
    [maximumCount, Math.ceil(beamWidth * 0.7)],
    [maximumCount - 1, Math.ceil(beamWidth * 0.2)],
    [maximumCount - 2, Math.max(1, Math.floor(beamWidth * 0.1))],
  ]);
  const selected = [];
  const selectedSet = new Set();
  const usedByCount = new Map();
  sorted.forEach((state) => {
    const quota = quotas.get(state.scheduledCount) || 0;
    const used = usedByCount.get(state.scheduledCount) || 0;
    if (used >= quota || selected.length >= beamWidth) return;
    selected.push(state);
    selectedSet.add(state);
    usedByCount.set(state.scheduledCount, used + 1);
  });
  for (const state of sorted) {
    if (selected.length >= beamWidth) break;
    if (!selectedSet.has(state)) selected.push(state);
  }
  return selected.sort(stateComparator);
};

const timelineMetrics = (
  teachers,
  timelines,
  travelMinutes,
  paddingMinutes = 0,
  maxDriveMinutes = null
) => {
  let driveMinutes = 0;
  let idleMinutes = 0;
  const missingPairs = [];
  const activeTeacherDays = new Set();
  teachers.forEach((teacher) => {
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const events = timelines[timelineKey(teacher.id, dayIndex)] || [];
      const availabilityBlocks = teacher.availabilityByDay[dayIndex] || [];
      for (let blockIndex = 0; blockIndex < availabilityBlocks.length; blockIndex += 1) {
        const block = availabilityBlocks[blockIndex];
        const { events: inBlock, stops: route } = blockRouteStops(
          teacher,
          dayIndex,
          block,
          blockIndex,
          events,
          travelMinutes,
          paddingMinutes,
          maxDriveMinutes
        );
        if (!inBlock.length) continue;
        activeTeacherDays.add(`${teacher.id}|${dayIndex}`);
        for (let index = 0; index < route.length - 1; index += 1) {
          const from = route[index];
          const to = route[index + 1];
          const drive = safeTravel(travelMinutes, from.placeKey, to.placeKey);
          if (drive == null) {
            missingPairs.push({
              teacherId: teacher.id,
              dayIndex,
              blockIndex,
              fromKey: from.placeKey,
              toKey: to.placeKey,
            });
            continue;
          }
          driveMinutes += drive;
          if (!from.boundary && !to.boundary) {
            idleMinutes += Math.max(0, to.start - from.end - drive);
          }
        }
      }
    }
  });
  return {
    driveMinutes,
    idleMinutes,
    activeTeacherDays: activeTeacherDays.size,
    activeTeacherDayKeys: Array.from(activeTeacherDays).sort(),
    completeDriveData: missingPairs.length === 0,
    missingPairs,
  };
};

export const calculateScheduleMetrics = ({
  teachers: rawTeachers,
  fixedEvents = [],
  placements = [],
  travelMinutes,
  paddingMinutes = 0,
  maxDriveMinutes = null,
}) => {
  const teachers = (rawTeachers || []).map(prepareTeacher);
  const events = [
    ...prepareFixedEvents(fixedEvents),
    ...placements.map((placement, index) =>
      prepareEvent(
        {
          ...placement,
          start: placement.start ?? placement.startMinutes,
          end:
            placement.end ??
            placement.endMinutes ??
            Number(placement.startMinutes) + Number(placement.duration || placement.lessonLength || 30),
          optimized: true,
        },
        `metric-placement-${index}`
      )
    ),
  ];
  return timelineMetrics(
    teachers,
    buildTimelines(events),
    travelMinutes,
    paddingMinutes,
    maxDriveMinutes
  );
};

const containsEvent = (blocks, event) =>
  (blocks || []).some((block) => event.start >= block.start && event.end <= block.end);

export const validateOptimizedSchedule = ({
  teachers: rawTeachers,
  students: rawStudents,
  fixedEvents = [],
  placements = [],
  travelMinutes,
  paddingMinutes = 0,
  maxDriveMinutes = null,
}) => {
  const teachers = (rawTeachers || []).map(prepareTeacher);
  const teacherById = new Map(teachers.map((teacher) => [teacher.id, teacher]));
  const students = (rawStudents || []).map(prepareStudent);
  const studentByKey = new Map(students.map((student) => [student.key, student]));
  const preparedFixedEvents = prepareFixedEvents(fixedEvents);
  const fixedStudentKeys = new Set(
    preparedFixedEvents.map((event) => String(event.studentKey || "")).filter(Boolean)
  );
  const errors = [];
  const warnings = [];
  const warningSet = new Set();
  const addWarning = (message) => {
    if (!message || warningSet.has(message)) return;
    warningSet.add(message);
    warnings.push(message);
  };
  const seenStudents = new Set();
  const preparedPlacements = placements.map((placement, index) =>
    prepareEvent(
      {
        ...placement,
        start: placement.start ?? placement.startMinutes,
        end:
          placement.end ??
          placement.endMinutes ??
          Number(placement.startMinutes) + Number(placement.duration || placement.lessonLength || 30),
        optimized: true,
      },
      `validated-placement-${index}`
    )
  );

  preparedPlacements.forEach((placement) => {
    const studentKey = String(placement.studentKey || "");
    const student = studentByKey.get(studentKey);
    const teacher = teacherById.get(String(placement.teacherId));
    if (!student) errors.push(`Unknown student ${placement.studentKey}.`);
    if (!teacher) errors.push(`Unknown teacher ${placement.teacherId}.`);
    if (fixedStudentKeys.has(studentKey)) {
      errors.push(`Student ${placement.studentKey} already has a fixed lesson.`);
    }
    if (seenStudents.has(studentKey)) {
      errors.push(`Student ${placement.studentKey} is placed more than once.`);
    }
    seenStudents.add(studentKey);
    if (!student || !teacher) return;
    if (
      !Number.isInteger(placement.dayIndex) ||
      placement.dayIndex < 0 ||
      placement.dayIndex > 6 ||
      placement.start == null ||
      placement.end == null ||
      !Number.isFinite(placement.start) ||
      !Number.isFinite(placement.end) ||
      placement.end <= placement.start
    ) {
      errors.push(`Student ${student.key} has an invalid lesson time.`);
      return;
    }
    if (placement.end - placement.start !== student.duration) {
      errors.push(
        `Student ${student.key}'s placement is not the required ${student.duration} minutes.`
      );
    }
    if (
      !placement.placeKey ||
      !student.placeKey ||
      String(placement.placeKey) !== String(student.placeKey)
    ) {
      errors.push(`Student ${student.key} is assigned to the wrong location.`);
    }
    if (!teacherCanTeach(teacher, student)) {
      errors.push(
        `Teacher ${teacherDisplayName(teacher)} cannot teach student ${student.key}'s instrument.`
      );
    }
    if (!containsEvent(teacher.availabilityByDay[placement.dayIndex], placement)) {
      errors.push(
        `Student ${student.key} is outside teacher ${teacherDisplayName(teacher)}'s availability.`
      );
    }
    if (
      student.availabilityRestricted &&
      !containsEvent(student.availabilityByDay[placement.dayIndex], placement)
    ) {
      errors.push(`Student ${student.key} is outside the family's availability.`);
    }
  });

  const timelines = buildTimelines([
    ...preparedFixedEvents,
    ...preparedPlacements,
  ]);
  teachers.forEach((teacher) => {
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const dayEvents = timelines[timelineKey(teacher.id, dayIndex)] || [];
      for (let index = 0; index < dayEvents.length - 1; index += 1) {
        const current = dayEvents[index];
        const next = dayEvents[index + 1];
        if (current.end > next.start) {
          if (!current.optimized && !next.optimized) {
            addWarning(
              `Teacher ${teacherDisplayName(teacher)}'s existing ${DAY_NAMES[dayIndex]} calendar contains overlapping fixed events. Those events were left unchanged.`
            );
          } else {
            errors.push(
              `The proposed schedule gives Teacher ${teacherDisplayName(teacher)} overlapping lessons on ${DAY_NAMES[dayIndex]}.`
            );
          }
        }
      }
      (teacher.availabilityByDay[dayIndex] || []).forEach((block, blockIndex) => {
        const { events: route, stops } = blockRouteStops(
          teacher,
          dayIndex,
          block,
          blockIndex,
          dayEvents,
          travelMinutes,
          paddingMinutes,
          maxDriveMinutes
        );
        if (!route.length) return;
        for (let index = 0; index < stops.length - 1; index += 1) {
          const from = stops[index];
          const to = stops[index + 1];
          const inheritedCalendarLeg = !from.optimized && !to.optimized;
          const drive = safeTravel(travelMinutes, from.placeKey, to.placeKey);
          if (drive == null) {
            const message = `Missing drive time ${from.placeKey || "unknown"} → ${to.placeKey || "unknown"}.`;
            if (inheritedCalendarLeg) {
              addWarning(
                `Teacher ${teacherDisplayName(teacher)}'s existing ${DAY_NAMES[dayIndex]} calendar route is missing a stored drive time. Its fixed events were left unchanged.`
              );
            } else {
              errors.push(message);
            }
            continue;
          }
          if (maxDriveMinutes != null && drive > maxDriveMinutes) {
            if (inheritedCalendarLeg) {
              addWarning(
                `Teacher ${teacherDisplayName(teacher)}'s existing ${DAY_NAMES[dayIndex]} calendar route includes a ${drive}-minute leg beyond the ${maxDriveMinutes}-minute limit. Its fixed events were left unchanged.`
              );
            } else {
              errors.push(`A ${drive}-minute drive exceeds the ${maxDriveMinutes}-minute limit.`);
            }
          }
          if (from.end + drive + paddingMinutes > to.start) {
            if (inheritedCalendarLeg) {
              addWarning(
                `Teacher ${teacherDisplayName(teacher)}'s existing ${DAY_NAMES[dayIndex]} calendar route has less time between fixed events than the stored drive estimate. Its fixed events were left unchanged.`
              );
            } else {
              errors.push(
                `The proposed schedule leaves Teacher ${teacherDisplayName(teacher)} insufficient travel time on ${DAY_NAMES[dayIndex]}.`
              );
            }
          }
        }
      });
    }
  });

  return { valid: errors.length === 0, errors, warnings };
};

const diagnoseUnassigned = (
  student,
  teachers,
  finalTimelines,
  feasibleOptions,
  {
    travelMinutes,
    slotMinutes,
    paddingMinutes,
    maxDriveMinutes,
  }
) => {
  if (!student.placeKey) return "Missing family location";
  const capable = teachers.filter((teacher) => teacherCanTeach(teacher, student));
  if (!student.instrumentKeys.size) return "Missing instrument";
  if (!capable.length) return "No active teacher can teach this instrument";
  if (!capable.some((teacher) => Object.values(teacher.availabilityByDay).some((blocks) => blocks.length))) {
    return "Matched teachers have no availability";
  }
  if (
    student.availabilityRestricted &&
    !Object.values(student.availabilityByDay).some((blocks) => blocks.length)
  ) {
    return "Family availability has no open times";
  }
  const sharedWindows = [];
  capable.forEach((teacher) => {
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      (teacher.availabilityByDay[dayIndex] || []).forEach((block) => {
        if (hasSharedAvailabilityWindowInBlock(student, dayIndex, block)) {
          sharedWindows.push({ teacher, dayIndex, block });
        }
      });
    }
  });
  if (!sharedWindows.length) {
    return `No teacher and family availability overlap for a ${student.duration}-minute lesson`;
  }

  const hasCompleteHomeRoute = sharedWindows.some(({ teacher }) => {
    const outbound = safeTravel(travelMinutes, teacher.homeKey, student.placeKey);
    const inbound = safeTravel(travelMinutes, student.placeKey, teacher.homeKey);
    return outbound != null && inbound != null;
  });
  if (!hasCompleteHomeRoute) {
    return "Missing travel time between the family and compatible teacher homes";
  }
  if (feasibleOptions.length) {
    return "Could not place without removing another scheduled student";
  }

  const enumerateWith = ({
    timelines = finalTimelines,
    padding = paddingMinutes,
    maxDrive = maxDriveMinutes,
  } = {}) =>
    enumeratePlacementOptions({
      student,
      teachers,
      timelines,
      travelMinutes,
      slotMinutes,
      paddingMinutes: padding,
      maxDriveMinutes: maxDrive,
      maxStartsPerGap: Infinity,
    });

  if (
    maxDriveMinutes != null &&
    enumerateWith({ maxDrive: null }).length
  ) {
    return `The max-drive limit is too low for every otherwise feasible route`;
  }
  if (paddingMinutes > 0 && enumerateWith({ padding: 0 }).length) {
    return `The ${paddingMinutes}-minute travel padding leaves no feasible slot`;
  }

  const emptyTimelines = {};
  if (enumerateWith({ timelines: emptyTimelines }).length) {
    return "Existing calendar or locked lessons leave no conflict-free slot with enough travel time";
  }
  if (
    enumerateWith({
      timelines: emptyTimelines,
      padding: 0,
      maxDrive: null,
    }).length
  ) {
    return "Travel limits leave no feasible slot even on an otherwise empty teacher day";
  }
  return "Teacher availability is too short for the lesson and required travel";
};

/**
 * Returns the directed drive pairs that could participate in a route. Pairs
 * are scoped per compatible teacher, which avoids paying for impossible
 * cross-instrument routes while still including student-to-student travel.
 */
export const collectRequiredDrivePairs = ({
  teachers: rawTeachers,
  students: rawStudents,
  fixedEvents = [],
  travelMinutes,
  paddingMinutes = 0,
  maxDriveMinutes = null,
}) => {
  const teachers = (rawTeachers || []).map(prepareTeacher);
  const students = (rawStudents || []).map(prepareStudent);
  const events = prepareFixedEvents(fixedEvents);
  const pairs = [];
  const seen = new Set();
  const addMissingPair = (fromKey, toKey) => {
    if (!fromKey || !toKey || String(fromKey) === String(toKey)) return;
    if (safeTravel(travelMinutes, fromKey, toKey) != null) return;
    const key = `${fromKey}=>${toKey}`;
    if (seen.has(key)) return;
    seen.add(key);
    pairs.push({ fromKey, toKey });
  };
  const canPrecede = (left, right) =>
    left.positions.some((leftPosition) =>
      right.positions.some(
        (rightPosition) =>
          leftPosition.earliestEnd <= rightPosition.latestStart
      )
    );
  teachers.forEach((teacher) => {
    if (!teacher.homeKey) return;
    for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
      const dayEvents = events.filter(
        (event) => event.teacherId === teacher.id && event.dayIndex === dayIndex
      );
      (teacher.availabilityByDay[dayIndex] || []).forEach(
        (teacherBlock, blockIndex) => {
          const studentNodes = students
            .filter(
              (student) =>
                student.placeKey &&
                teacherCanTeach(teacher, student) &&
                hasSharedAvailabilityWindowInBlock(
                  student,
                  dayIndex,
                  teacherBlock
                )
            )
            .map((student) => {
              const allowed = student.availabilityRestricted
                ? (student.availabilityByDay[dayIndex] || [])
                    .map((block) => intersects(block, teacherBlock))
                    .filter((block) => block.end - block.start >= student.duration)
                : [teacherBlock];
              return {
                key: student.placeKey,
                positions: allowed.map((block) => ({
                  earliestEnd: block.start + student.duration,
                  latestStart: block.end - student.duration,
                })),
              };
            });
          const { events: touchingEvents, routeEvents } = blockRouteStops(
            teacher,
            dayIndex,
            teacherBlock,
            blockIndex,
            dayEvents,
            travelMinutes,
            paddingMinutes,
            maxDriveMinutes
          );
          if (!studentNodes.length && !touchingEvents.length) return;
          const eventNodes = routeEvents
            .filter((event) => event.placeKey)
            .map((event) => ({
              key: event.placeKey,
              positions: [
                { earliestEnd: event.end, latestStart: event.start },
              ],
            }));
          const nodes = [...studentNodes, ...eventNodes];
          const homeStart = {
            positions: [
              { earliestEnd: teacherBlock.start, latestStart: teacherBlock.start },
            ],
          };
          const homeEnd = {
            positions: [
              { earliestEnd: teacherBlock.end, latestStart: teacherBlock.end },
            ],
          };
          nodes.forEach((node) => {
            if (canPrecede(homeStart, node)) {
              addMissingPair(teacher.homeKey, node.key);
            }
            if (canPrecede(node, homeEnd)) {
              addMissingPair(node.key, teacher.homeKey);
            }
          });
          for (let leftIndex = 0; leftIndex < nodes.length; leftIndex += 1) {
            for (
              let rightIndex = leftIndex + 1;
              rightIndex < nodes.length;
              rightIndex += 1
            ) {
              const left = nodes[leftIndex];
              const right = nodes[rightIndex];
              if (canPrecede(left, right)) addMissingPair(left.key, right.key);
              if (canPrecede(right, left)) addMissingPair(right.key, left.key);
            }
          }
        }
      );
    }
  });
  return pairs;
};

export const optimizeWeeklySchedule = ({
  teachers: rawTeachers,
  students: rawStudents,
  fixedEvents = [],
  travelMinutes,
  slotMinutes = DEFAULT_SLOT_MINUTES,
  paddingMinutes = 0,
  maxDriveMinutes = null,
  beamWidth,
  maxOptionsPerState = DEFAULT_OPTIONS_PER_STATE,
  maxStartsPerGap,
  enableCoverageRepair = true,
}) => {
  const teachers = (rawTeachers || []).map(prepareTeacher).filter((teacher) => teacher.id);
  const preparedFixedEvents = prepareFixedEvents(fixedEvents);
  const fixedStudentKeys = new Set(
    preparedFixedEvents.map((event) => String(event.studentKey || "")).filter(Boolean)
  );
  const studentsByKey = new Map();
  (rawStudents || []).map(prepareStudent).forEach((student) => {
    if (
      student.key &&
      !fixedStudentKeys.has(student.key) &&
      !studentsByKey.has(student.key)
    ) {
      studentsByKey.set(student.key, student);
    }
  });
  const students = Array.from(studentsByKey.values());
  const estimatedCandidateCount = students.reduce((studentTotal, student) => {
    const studentCandidates = teachers.reduce((teacherTotal, teacher) => {
      if (!teacherCanTeach(teacher, student)) return teacherTotal;
      let count = 0;
      for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
        for (const teacherBlock of teacher.availabilityByDay[dayIndex] || []) {
          const allowedBlocks = student.availabilityRestricted
            ? (student.availabilityByDay[dayIndex] || [])
                .map((block) => intersects(block, teacherBlock))
                .filter((block) => block.end - block.start >= student.duration)
            : [teacherBlock];
          for (const block of allowedBlocks) {
            count += Math.max(
              0,
              Math.floor((block.end - block.start - student.duration) / slotMinutes) + 1
            );
          }
        }
      }
      return teacherTotal + count;
    }, 0);
    return studentTotal + studentCandidates;
  }, 0);
  const searchComplexity = Math.max(1, estimatedCandidateCount);
  const effectiveBeamWidth =
    beamWidth != null
      ? Math.max(1, Number(beamWidth) || 1)
      : students.length <= 8 && searchComplexity <= 300
      ? 30000
      : students.length <= 12 && searchComplexity <= 600
      ? 5000
      : students.length <= 20 && searchComplexity <= 2000
      ? 1000
      : DEFAULT_BEAM_WIDTH;
  const effectiveStartsPerGap =
    maxStartsPerGap != null
      ? maxStartsPerGap
      : students.length <= 12 && searchComplexity <= 600
      ? Infinity
      : students.length <= 20 && searchComplexity <= 800
      ? Infinity
      : DEFAULT_LARGE_SEARCH_STARTS_PER_GAP;
  const baseTimelines = buildTimelines(preparedFixedEvents);
  const baseMetrics = timelineMetrics(
    teachers,
    baseTimelines,
    travelMinutes,
    paddingMinutes,
    maxDriveMinutes
  );
  if (!baseMetrics.completeDriveData) {
    return {
      placements: [],
      unassigned: students.map((student) => ({
        studentKey: student.key,
        reason: "Missing drive information for an existing route",
      })),
      metrics: { ...baseMetrics, scheduledCount: 0, totalStudents: students.length },
      valid: false,
      errors: ["Existing calendar routes have missing drive information."],
    };
  }

  const initialState = {
    timelines: baseTimelines,
    placements: [],
    assignedKeys: new Set(),
    scheduledCount: 0,
    driveMinutes: baseMetrics.driveMinutes,
    idleMinutes: baseMetrics.idleMinutes,
    churnCount: 0,
    activeTeacherDays: new Set(baseMetrics.activeTeacherDayKeys || []),
    activeTeacherDayCount: baseMetrics.activeTeacherDays,
    tieKey: "",
  };

  const studentsWithCounts = students.map((student) => ({
    student,
    count: enumeratePlacementOptions({
      student,
      teachers,
      timelines: baseTimelines,
      travelMinutes,
      slotMinutes,
      paddingMinutes,
      maxDriveMinutes,
      maxStartsPerGap: effectiveStartsPerGap,
    }).length,
  }));
  studentsWithCounts.sort(
    (a, b) =>
      a.count - b.count ||
      b.student.duration - a.student.duration ||
      a.student.key.localeCompare(b.student.key)
  );

  let beam = [initialState];
  studentsWithCounts.forEach(({ student }) => {
    const nextStates = [];
    beam.forEach((state) => {
      const options = selectDiverseOptions(
        enumeratePlacementOptions({
          student,
          teachers,
          timelines: state.timelines,
          travelMinutes,
          slotMinutes,
          paddingMinutes,
          maxDriveMinutes,
          maxStartsPerGap: effectiveStartsPerGap,
        }),
        maxOptionsPerState
      );
      options.forEach((option) => nextStates.push(insertOption(state, student, option)));
      nextStates.push({
        ...state,
        tieKey: `${state.tieKey}|${student.key}:unassigned`,
      });
    });
    beam = pruneBeam(nextStates, effectiveBeamWidth);
  });

  let best = beam.sort(stateComparator)[0] || initialState;

  // A final coverage sweep ensures no student remains unassigned when it can
  // simply be inserted into the best complete state.
  let added = true;
  while (added) {
    added = false;
    const currentBest = best;
    const remaining = students.filter(
      (student) => !currentBest.assignedKeys.has(student.key)
    );
    const candidates = remaining
      .map((student) => ({
        student,
        options: enumeratePlacementOptions({
          student,
          teachers,
          timelines: currentBest.timelines,
          travelMinutes,
          slotMinutes,
          paddingMinutes,
          maxDriveMinutes,
          maxStartsPerGap: Infinity,
        }),
      }))
      .filter((entry) => entry.options.length)
      .sort(
        (a, b) =>
          a.options.length - b.options.length || a.student.key.localeCompare(b.student.key)
      );
    if (candidates.length) {
      best = insertOption(best, candidates[0].student, candidates[0].options[0]);
      added = true;
    }
  }

  const sortPlacements = (items) =>
    items
    .slice()
    .sort(
      (a, b) =>
        a.teacherId.localeCompare(b.teacherId) ||
        a.dayIndex - b.dayIndex ||
        a.startMinutes - b.startMinutes ||
        a.studentKey.localeCompare(b.studentKey)
    );
  let placements = sortPlacements(best.placements);

  // Large school-wide searches intentionally cap their beam and sampled start
  // times. If that leaves a student out, retry the small connected route
  // around that student with full start enumeration. This recovers tight local
  // packings without making an unrelated large teacher network pay the cost of
  // an exhaustive global retry.
  if (enableCoverageRepair && placements.length < students.length) {
    const maximumRepairComponentStudents = 13;
    const initialOptionCount = new Map(
      studentsWithCounts.map(({ student, count }) => [student.key, count])
    );
    const eligibleRouteKeysByStudent = new Map();
    const teacherIdByRouteKey = new Map();
    students.forEach((student) => {
      const routeKeys = new Set();
      teachers.forEach((teacher) => {
        if (!teacher.homeKey || !teacherCanTeach(teacher, student)) return;
        for (let dayIndex = 0; dayIndex < 7; dayIndex += 1) {
          const canUseDay = (teacher.availabilityByDay[dayIndex] || []).some(
            (block) =>
              hasSharedAvailabilityWindowInBlock(student, dayIndex, block)
          );
          if (!canUseDay) continue;
          const routeKey = timelineKey(teacher.id, dayIndex);
          routeKeys.add(routeKey);
          teacherIdByRouteKey.set(routeKey, teacher.id);
        }
      });
      eligibleRouteKeysByStudent.set(student.key, routeKeys);
    });
    const attemptedComponentStates = new Set();
    const buildRepairComponent = ({
      target,
      currentPlacements,
      unassignedStudents,
      includeUnassignedPeers,
    }) => {
      const relevantRouteKeys = new Set(
        eligibleRouteKeysByStudent.get(target.key) || []
      );
      const componentKeys = new Set([target.key]);
      let componentExpanded = true;
      while (
        componentExpanded &&
        componentKeys.size <= maximumRepairComponentStudents
      ) {
        componentExpanded = false;
        const studentKeys = Array.from(componentKeys);
        for (const studentKey of studentKeys) {
          const routeKeys = Array.from(
            eligibleRouteKeysByStudent.get(studentKey) || []
          );
          for (const routeKey of routeKeys) {
            if (relevantRouteKeys.has(routeKey)) continue;
            relevantRouteKeys.add(routeKey);
            componentExpanded = true;
          }
        }
        for (const placement of currentPlacements) {
          if (
            !relevantRouteKeys.has(
              timelineKey(placement.teacherId, placement.dayIndex)
            ) ||
            componentKeys.has(placement.studentKey)
          ) {
            continue;
          }
          componentKeys.add(placement.studentKey);
          componentExpanded = true;
        }
        if (!includeUnassignedPeers) continue;
        for (const student of unassignedStudents) {
          if (componentKeys.has(student.key)) continue;
          const routeKeys = Array.from(
            eligibleRouteKeysByStudent.get(student.key) || []
          );
          const sharesRoute = routeKeys.some((routeKey) =>
            relevantRouteKeys.has(routeKey)
          );
          if (!sharesRoute) continue;
          componentKeys.add(student.key);
          componentExpanded = true;
        }
      }
      return { componentKeys, relevantRouteKeys };
    };
    const attemptRepairComponent = ({
      componentKeys,
      relevantRouteKeys,
      currentPlacements,
    }) => {
      if (
        !relevantRouteKeys.size ||
        componentKeys.size > maximumRepairComponentStudents
      ) {
        return null;
      }
      const relevantTeacherIds = new Set();
      for (const routeKey of relevantRouteKeys) {
        const teacherId = teacherIdByRouteKey.get(routeKey);
        if (teacherId) relevantTeacherIds.add(teacherId);
      }
      if (!relevantTeacherIds.size) return null;
      const currentComponentPlacements = currentPlacements.filter((placement) =>
        componentKeys.has(placement.studentKey)
      );
      const relevantPlacementState = currentPlacements.filter((placement) =>
        relevantTeacherIds.has(placement.teacherId)
      );
      const componentStateKey = [
        Array.from(componentKeys).sort().join(","),
        Array.from(relevantRouteKeys).sort().join(","),
        relevantPlacementState
          .map(
            (placement) =>
              `${placement.studentKey}@${placement.teacherId}:${placement.dayIndex}:${placement.startMinutes}`
          )
          .sort()
          .join(","),
      ].join("|");
      if (attemptedComponentStates.has(componentStateKey)) return null;
      attemptedComponentStates.add(componentStateKey);

      const componentStudents = students.filter((student) =>
        componentKeys.has(student.key)
      );
      const relevantTeachers = teachers.filter((teacher) =>
        relevantTeacherIds.has(teacher.id)
      );
      const outsidePlacements = currentPlacements.filter(
        (placement) =>
          relevantTeacherIds.has(placement.teacherId) &&
          !componentKeys.has(placement.studentKey)
      );
      const repairFixedEvents = [
        ...preparedFixedEvents.filter((event) =>
          relevantTeacherIds.has(event.teacherId)
        ),
        ...outsidePlacements.map((placement, index) => ({
          id: `repair-fixed-${placement.studentKey}-${index}`,
          studentKey: placement.studentKey,
          teacherId: placement.teacherId,
          dayIndex: placement.dayIndex,
          start: placement.startMinutes,
          end: placement.endMinutes,
          placeKey: placement.placeKey,
        })),
      ];
      const repaired = optimizeWeeklySchedule({
        teachers: relevantTeachers,
        students: componentStudents,
        fixedEvents: repairFixedEvents,
        travelMinutes,
        slotMinutes,
        paddingMinutes,
        maxDriveMinutes,
        beamWidth:
          componentStudents.length === maximumRepairComponentStudents
            ? 1000
            : 5000,
        maxOptionsPerState: Math.max(maxOptionsPerState, 56),
        maxStartsPerGap: Infinity,
        enableCoverageRepair: false,
      });
      if (
        !repaired.valid ||
        repaired.placements.length <= currentComponentPlacements.length
      ) {
        return null;
      }
      const candidatePlacements = sortPlacements([
        ...currentPlacements.filter(
          (placement) => !componentKeys.has(placement.studentKey)
        ),
        ...repaired.placements,
      ]);
      const candidateValidation = validateOptimizedSchedule({
        teachers,
        students,
        fixedEvents: preparedFixedEvents,
        placements: candidatePlacements,
        travelMinutes,
        paddingMinutes,
        maxDriveMinutes,
      });
      return candidateValidation.valid ? candidatePlacements : null;
    };
    let madeProgress = true;
    while (madeProgress && placements.length < students.length) {
      madeProgress = false;
      const placedKeys = new Set(placements.map((placement) => placement.studentKey));
      const repairTargets = students
        .filter(
          (student) =>
            !placedKeys.has(student.key) &&
            (initialOptionCount.get(student.key) || 0) > 0
        )
        .sort(
          (a, b) =>
            (initialOptionCount.get(a.key) || 0) -
              (initialOptionCount.get(b.key) || 0) ||
            a.key.localeCompare(b.key)
        );

      for (const target of repairTargets) {
        const minimalComponent = buildRepairComponent({
          target,
          currentPlacements: placements,
          unassignedStudents: repairTargets,
          includeUnassignedPeers: false,
        });
        let candidatePlacements = attemptRepairComponent({
          ...minimalComponent,
          currentPlacements: placements,
        });

        // If repairing one target is not enough, retry all unassigned peers in
        // the same connected route together. Keep the minimal attempt first so
        // impossible peers cannot push an otherwise repairable route over the
        // bounded component size.
        if (!candidatePlacements) {
          const expandedComponent = buildRepairComponent({
            target,
            currentPlacements: placements,
            unassignedStudents: repairTargets,
            includeUnassignedPeers: true,
          });
          candidatePlacements = attemptRepairComponent({
            ...expandedComponent,
            currentPlacements: placements,
          });
        }
        if (!candidatePlacements) continue;
        placements = candidatePlacements;
        madeProgress = true;
        break;
      }
    }
  }

  const finalPlacementEvents = placements.map((placement, index) =>
    prepareEvent(
      {
        ...placement,
        start: placement.startMinutes,
        end: placement.endMinutes,
        optimized: true,
      },
      `final-placement-${index}`
    )
  );
  const finalTimelines = buildTimelines([
    ...preparedFixedEvents,
    ...finalPlacementEvents,
  ]);
  const finalAssignedKeys = new Set(
    placements.map((placement) => placement.studentKey)
  );
  const unassigned = students
    .filter((student) => !finalAssignedKeys.has(student.key))
    .map((student) => {
      const options = enumeratePlacementOptions({
        student,
        teachers,
        timelines: finalTimelines,
        travelMinutes,
        slotMinutes,
        paddingMinutes,
        maxDriveMinutes,
        maxStartsPerGap: Infinity,
      });
      return {
        studentKey: student.key,
        reason: diagnoseUnassigned(
          student,
          teachers,
          finalTimelines,
          options,
          {
            travelMinutes,
            slotMinutes,
            paddingMinutes,
            maxDriveMinutes,
          }
        ),
      };
    });

  const validation = validateOptimizedSchedule({
    teachers,
    students,
    fixedEvents: preparedFixedEvents,
    placements,
    travelMinutes,
    paddingMinutes,
    maxDriveMinutes,
  });
  const finalMetrics = timelineMetrics(
    teachers,
    finalTimelines,
    travelMinutes,
    paddingMinutes,
    maxDriveMinutes
  );
  const placementByStudent = new Map(
    placements.map((placement) => [placement.studentKey, placement])
  );
  const movedCount = students.reduce((count, student) => {
    const incumbent = student.incumbentPlacement;
    if (!incumbent) return count;
    const placement = placementByStudent.get(student.key);
    const unchanged =
      placement &&
      String(placement.teacherId) === String(incumbent.teacherId) &&
      Number(placement.dayIndex) === Number(incumbent.dayIndex) &&
      Number(placement.startMinutes) === Number(incumbent.startMinutes);
    return count + (unchanged ? 0 : 1);
  }, 0);
  return {
    placements,
    unassigned,
    metrics: {
      ...finalMetrics,
      scheduledCount: placements.length,
      totalStudents: students.length,
      movedCount,
      baseDriveMinutes: baseMetrics.driveMinutes,
      addedDriveMinutes: finalMetrics.driveMinutes - baseMetrics.driveMinutes,
    },
    valid: validation.valid,
    errors: validation.errors,
    warnings: validation.warnings,
  };
};

export { DAY_NAMES };
